<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>
    <div v-else>
      <BackButton
          :label="$t('general.button.back')"
          :to="{ name: 'analyticsHome' }"
          marginBottom="5"/>

      <div class="d-flex justify-content-between">
        <h6 class="element-header">
          Rechnungslegung - Übersicht
        </h6>
      </div>

      <div class="element-box">
        <h5 class="form-header mb-4">Soziotherapie</h5>

        <Search
            :filters="filters"
            :loading="isSearching"
            :officeDropdown="officeDropdown"
            :unemployedDropdown="unemployedDropdown"
            :clientDropdown="clientDropdown"
            @search="getMeetingList"/>

        <div class="row align-items-center justify-content-between">
          <div class="mt-3">
            <PerPage :values="[10, 25, 50, 100]" @update:perPage="getMeetingList"/>

            <ExcelExportButton
                style="margin-right: 15px;"
                :label="$t('general.excel_export')"
                :loading="isExcelLoaded"
                @click="getExcelExport"/>
          </div>
        </div>

        <div class="row mt-4">
          <div class="col-12">
            <p style="font-size: 16px; margin-bottom: 5px;"> Geleistete Einheiten: {{ sumUnits }}</p>
            <p style="font-size: 16px">Gesamtbetrag: {{ totalAmount }}<span class="ms-1">&euro;</span></p>
          </div>
        </div>

        <div class="table-responsive overflow-auto mt-4 mb-3">
          <table class="table table-striped">
            <thead>
            <tr>
              <th>Kostenträger</th>
              <th>Standort</th>
              <th>KST 1</th>
              <th>KST 2</th>
              <th>Kunden</th>
              <th>JC / AA Nummer</th>
              <th>Art des Termins</th>
              <th>Termin</th>
              <th class="text-end">Einheiten</th>
              <th class="text-end">Gesamtbetrag</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(data, index) in meetingsList.data" :key="index">
              <td>{{ data?.client }}</td>
              <td>{{ data?.location}}</td>
              <td>{{ data?.costCenter1 }}</td>
              <td>SOZ</td>
              <td>{{ data?.unemployedFirstName }} {{ data?.unemployedLastName }}</td>
              <td>{{ data?.unemployedAaNumber }}</td>
              <td>
                <div v-if="data?.meetingType">
                  <span v-if="data.operationType === 'sociotherapie'">
                    {{ $t(`socio_therapy_meeting_type2.${data.meetingType}`) }}
                  </span>
                  <span v-else>
                    {{ $t(`socio_therapy_meeting_type.${data.meetingType}`) }}
                  </span>
                </div>
              </td>
              <td>{{ data?.termin }}</td>
              <td class="text-end">{{ data?.units }}</td>
              <td class="text-end">{{ data?.totalForMeeting?.toFixed(2) }}</td>
            </tr>
            </tbody>
          </table>
        </div>
        <Pagination
            v-if="meetingsList?.pagination?.total_pages > 1"
            :totalPages="meetingsList?.pagination?.total_pages"
            :currentPage="meetingsList?.pagination?.current_page"
            :totalItems="meetingsList?.pagination?.total_items"
            :perPage="meetingsList?.pagination?.items_per_page"
            @pagechanged="getMeetingList"/>
      </div>
    </div>
  </PageTemplate>
</template>

<script setup>
import analyticsService from '@/api/Analytics';
import Loader from '@/components/Loader.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import Pagination from '@/components/Pagination.vue';
import PerPage from '@/components/PerPage.vue';
import { CloseToast, ErrorToast } from '@/components/VueToast';
import { getAllUnemployedDropdown, getClientDropdown, getOfficeDropdown } from '@/shared/dropdowns';
import { downloadFile } from '@/shared/functions';
import Search from '@/views/App/Analytics/Accounting/Search.vue';
import { onMounted, ref } from 'vue';
import ExcelExportButton from "@/components/ExcelExportButton.vue";
import BackButton from "@/components/button/BackButton.vue";

const currentDate = new Date().getFullYear();

const filters = ref({
  'officeIds[]': [],
  clientId: '',
  unemployedId: '',
  startDate: `01.01.${currentDate}`,
  endDate: `31.12.${currentDate}`,
});

const meetingsList       = ref([]);
const officeDropdown     = ref([]);
const clientDropdown     = ref([]);
const unemployedDropdown = ref([]);
const sumUnits           = ref(0);
const totalAmount        = ref(0);
const isSearching        = ref(false);
const isLoaded           = ref(false);
const isExcelLoaded      = ref(false);

async function getMeetingList(page = 1, perPage, passedFilters) {
  CloseToast();

  if (passedFilters) {
    isSearching.value = true;
  }
  const perPageRes = meetingsList.value?.pagination?.items_per_page;

  try {
    const { data } = await analyticsService.getAccountingSocioTherapyCoachingList({
      page: page,
      limit: perPage ?? perPageRes,
      ...(passedFilters ? passedFilters : filters.value),
    });

    meetingsList.value = data?.list;
    sumUnits.value     = data?.sumUnits;
    totalAmount.value  = data?.totalAmount;
    isSearching.value  = false;
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

async function getExcelExport() {
  await downloadFile(`/analytics/accounting/socio-therapy-coaching/excel-export/${sumUnits.value}/${totalAmount.value}`,
      undefined,
      {
        ...filters.value,
        'officeIds[]': filters.value['officeIds[]'],
      },
      false,
      'GET',
      isExcelLoaded
  )
}

onMounted(async () => {
  await Promise.all([
    getMeetingList(),
    getOfficeDropdown(officeDropdown),
    getAllUnemployedDropdown(unemployedDropdown),
    getClientDropdown(clientDropdown),
  ]);

  isLoaded.value = true;
});
</script>

<style scoped>

</style>